import { useState } from 'react'
import { NavLink, Outlet } from 'react-router-dom'
import { Clock, CalendarDays, BadgeDollarSign, Settings, LogOut, FileText } from 'lucide-react'
import { useMe, useDisplayName } from '../lib/me.js'
import { nameInitial } from '../lib/text.js'
import { clearAllRecentPatients } from '../lib/recentPatients.js'
import ConfirmDialog from './ConfirmDialog.jsx'
import { useNotice } from '../lib/notice.js'

// Tab shell for the self-service area (พื้นที่ของฉัน) — the one place a staff
// member manages their OWN record, as opposed to /manager/employee which is HR
// looking at everybody. Mirrors EmployeeLayout's tab pattern so the two read as
// the same product.
//
// ออกจากระบบ sits in this row because the spec lists it as a fifth item, but it
// is an ACTION, not a tab: it is pushed to the right, tinted rose, and confirms
// first. Making it look like a tab would put "leave the app" one stray click
// from "ตั้งค่า".
// 🔑 ฝ่ายบุคคล holds ขอลา · ประเมิน · หนังสือเตือน behind a nested pill row
// (MeHrLayout). การลาออก is NOT among them — it sits in ตั้งค่า, beside the employment
// facts it changes, and deliberately nowhere near ขอลา.
//
// They were four separate tabs here, which pushed this row to seven AND put **ขอลา
// next to ลาออก**: one syllable apart in Thai, meaning very different things, and the
// misclick nobody would notice until it had happened. Grouping them fixes both — the
// row is back to five, and those two now sit in a sub-nav that gets read rather than
// glanced at.
const TABS = [
  { to: 'clockin',  label: 'ลงเวลา',    icon: Clock,           hint: 'เข้า-ออกงานของฉัน' },
  { to: 'schedule', label: 'ตารางเวร',  icon: CalendarDays,    hint: 'ตารางเวรและกะที่ได้รับ' },
  { to: 'salary',   label: 'เงินเดือน', icon: BadgeDollarSign, hint: 'สลิปเงินเดือนของฉัน' },
  { to: 'hr',       label: 'ฝ่ายบุคคล', icon: FileText,        hint: 'ขอลา · ประเมิน · หนังสือเตือน', badge: 'hr' },
  { to: 'setting',  label: 'ตั้งค่า',    icon: Settings,        hint: 'ข้อมูลส่วนตัว บัญชีธนาคาร เอกสาร และการลาออก', badge: 'setting' },
]

export default function MeLayout() {
  const me = useMe()
  const [confirmOut, setConfirmOut] = useState(false)
  // The header names the PERSON, not the login — the employee record's Thai
  // ชื่อ-นามสกุล, not the latin account label. Same hook the sidebar card uses.
  const displayName = useDisplayName()
  // Read-only here — Layout.jsx owns the single autoLoad, so mounting this shell
  // does not kick a second identical request.
  const notice = useNotice()
  // Each tab shows only what is actually inside it, so a badge always leads somewhere.
  // การลาออก lives in ตั้งค่า, so its two counters travel with it — the sidebar dot
  // stays notice.total, which is exactly the sum of these, and the two cannot disagree.
  const badgeCount = (key) => {
    if (key === 'hr') return notice.warnings_unack + notice.leave_decided
    if (key === 'setting') return notice.resign_decided + notice.survey_pending
    return notice[key] || 0
  }

  function logout() {
    clearAllRecentPatients()
    window.location.href = '/__app/logout'
  }

  return (
    <div className="px-4 sm:px-10 py-6 sm:py-8">
      {/* Identity header — who this page is about, so the "my" in every tab is literal. */}
      <div className="flex items-center gap-3.5 mb-5">
        <div className="w-12 h-12 rounded-2xl bg-slate-100 text-slate-500 flex items-center justify-center overflow-hidden flex-none ring-1 ring-slate-200/70">
          {me?.image
            ? <img src={me.image} alt="" className="w-full h-full object-cover" />
            : <span className="text-[18px] font-semibold">{nameInitial(displayName || me?.email)}</span>}
        </div>
        <div className="min-w-0">
          <h1 className="text-[22px] font-semibold text-slate-900 tracking-tight leading-tight truncate">
            {displayName || 'พื้นที่ของฉัน'}
          </h1>
          <p className="text-[13.5px] text-slate-500 truncate">{me?.email || 'ข้อมูลและการทำงานของฉัน'}</p>
        </div>
      </div>

      {/* Sub-tab nav + logout */}
      <div className="border-b border-slate-200 -mx-1 mb-6">
        <nav className="flex flex-wrap items-end gap-x-1 px-1">
          {TABS.map(({ to, label, icon: Icon, hint, badge }) => (
            <NavLink
              key={to}
              to={`/me/${to}`}
              title={hint}
              className={({ isActive }) =>
                `relative flex items-center gap-1.5 px-2.5 sm:px-3 py-2.5 text-[13.5px] sm:text-[14px] font-medium whitespace-nowrap transition-colors ${
                  isActive ? 'text-brand-700' : 'text-slate-500 hover:text-slate-900'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <Icon size={14} strokeWidth={1.75} />
                  <span>{label}</span>
                  {badge && badgeCount(badge) > 0 && (
                    <span className="rounded-full bg-rose-500 text-white px-1.5 text-[10.5px] font-semibold leading-[16px]">
                      {badgeCount(badge)}
                    </span>
                  )}
                  {isActive && (
                    <span className="absolute left-2 right-2 -bottom-px h-[2px] bg-brand-600 rounded-full" />
                  )}
                </>
              )}
            </NavLink>
          ))}

          <button
            type="button"
            onClick={() => setConfirmOut(true)}
            title="ออกจากระบบ"
            className="ml-auto flex items-center gap-1.5 px-2.5 sm:px-3 py-2.5 text-[13.5px] sm:text-[14px] font-medium whitespace-nowrap text-slate-400 hover:text-rose-600 transition-colors"
          >
            <LogOut size={14} strokeWidth={1.75} />
            <span>ออกจากระบบ</span>
          </button>
        </nav>
      </div>

      <Outlet />

      {confirmOut && (
        <ConfirmDialog
          title="ออกจากระบบ?"
          message="คุณจะต้องเข้าสู่ระบบใหม่อีกครั้งเพื่อใช้งานต่อ"
          confirmLabel="ออกจากระบบ"
          tone="danger"
          onConfirm={logout}
          onCancel={() => setConfirmOut(false)}
        />
      )}
    </div>
  )
}
